/**
 * How a draft version names who made it and what it did. Agents' edits run
 * in an attorney's session, so `created_by` alone credits the attorney.
 */
import {
  type DraftVersion,
  type DraftVersionActor,
  type DraftVersionOperation,
  DraftVersionTrigger
} from "@/api/types/draft";

const ACTOR_NAME: Partial<Record<DraftVersionActor, string>> = {
  casey: "Casey",
  drafting_agent: "Drafting Agent",
  system: "CaseBlink"
};

const OPERATION_LABEL: Partial<Record<DraftVersionOperation, string>> = {
  tracked_proposal: "Proposed tracked changes",
  accept: "Accepted tracked changes",
  reject: "Rejected tracked changes",
  resolve: "Resolved tracked changes",
  sync: "Kept in sync with the case"
};

/** A stored person: first and last name, or their email. A bare uid is not a name. */
export const getRequesterName = (v: DraftVersion): string | null => {
  const p = v.created_by_profile;
  const first = (p?.first_name ?? "").trim();
  const last = (p?.last_name ?? "").trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const email = (p?.email ?? "").trim();
  return email || null;
};

const getPersonName = (v: DraftVersion): string => {
  const named = getRequesterName(v);
  if (named) return named;
  const uid = (v.created_by_profile?.uid ?? v.created_by ?? "").trim();
  return uid || "Unknown";
};

export const getDisplayName = (v: DraftVersion): string => {
  const agent = v.actor_kind ? ACTOR_NAME[v.actor_kind] : undefined;
  if (agent) return agent;
  return getPersonName(v);
};

const getVersionAction = (v: DraftVersion): string => {
  const op = v.operation ? OPERATION_LABEL[v.operation] : undefined;
  if (op) return op;
  if (v.operation === "generation" && v.actor_kind === "drafting_agent")
    return "Drafted";
  if (v.operation === "edit" && v.actor_kind === "casey") return "Edited";
  if (v.trigger === DraftVersionTrigger.SECTION_REGENERATE) {
    const section = (v.section_name ?? "").trim();
    return section ? `Regenerated ${section}` : "Regenerated a section";
  }
  if (v.trigger === DraftVersionTrigger.FULL_GENERATE)
    return "Full draft generate";
  if (v.trigger === DraftVersionTrigger.PRE_RESTORE)
    return "Snapshot before restore";
  if (v.trigger === DraftVersionTrigger.RESTORE) return "Draft restored";
  if (v.trigger === DraftVersionTrigger.MANUAL_SECTIONS_EDIT)
    return "Manual edit";
  if (v.trigger === DraftVersionTrigger.WORD_UPLOAD)
    return "Uploaded from Word";
  return "Updated";
};

export const getVersionSubtitle = (v: DraftVersion): string => {
  const what = getVersionAction(v);
  const asked =
    v.actor_kind === "casey" && (v.created_by_profile || v.created_by)
      ? getPersonName(v)
      : "";
  return asked && asked !== "Unknown" ? `${what} for ${asked}` : what;
};

export const showsPersonAvatar = (v: DraftVersion): boolean =>
  !v.actor_kind || v.actor_kind === "attorney";
